import { metrics } from '@opentelemetry/api';
import { Resource } from '@opentelemetry/resources';
import { MeterProvider, PeriodicExportingMetricReader, } from '@opentelemetry/sdk-metrics';
import { AggregationTemporalityPreference, OTLPMetricExporter, } from '@opentelemetry/exporter-metrics-otlp-http';
const EXPORT_INTERVAL_MS = 30_000;
class ClientMetrics {
    isServerSide = typeof window === 'undefined';
    isEnabled = typeof window === 'undefined';
    collectorUrl = 'unknown';
    serviceName = 'unknown';
    token = '';
    environment = undefined;
    version = undefined;
    #setupProperties({ serviceName, env, version, collectorUrl, samplingRate = 1, token, }) {
        this.collectorUrl = collectorUrl;
        this.serviceName = serviceName;
        this.environment = env;
        this.version = version;
        this.token = token;
        this.isEnabled = !this.isServerSide && Math.random() <= samplingRate;
    }
    #getResource() {
        const { serviceName, environment: env, version } = this;
        return Resource.default().merge(new Resource({
            'service.name': serviceName,
            version,
            env,
        }));
    }
    #getReaders() {
        const { collectorUrl } = this;
        const headers = {
            'Content-Type': 'application/json',
        };
        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }
        const readers = [
            new PeriodicExportingMetricReader({
                exporter: new OTLPMetricExporter({
                    url: `${collectorUrl}/v1/metrics`,
                    concurrencyLimit: 1,
                    headers,
                    temporalityPreference: AggregationTemporalityPreference.DELTA,
                }),
                exportIntervalMillis: EXPORT_INTERVAL_MS,
            }),
        ];
        return readers;
    }
    initialize(options) {
        this.#setupProperties(options);
        if (!this.isEnabled)
            return;
        const resource = this.#getResource();
        const readers = this.#getReaders();
        if (!this.isServerSide) {
            let hasFlushed = false;
            const flushReaders = () => {
                if (hasFlushed)
                    return;
                hasFlushed = true;
                readers.forEach(reader => reader.forceFlush());
            };
            const shutdownReaders = () => {
                if (hasFlushed)
                    return;
                hasFlushed = true;
                readers.forEach(reader => reader.shutdown());
            };
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    flushReaders();
                }
                else {
                    hasFlushed = false;
                }
            });
            window.addEventListener('pagehide', event => {
                if (event.persisted === false) {
                    shutdownReaders();
                }
            });
        }
        const meterProvider = new MeterProvider({
            resource,
            readers,
        });
        metrics.setGlobalMeterProvider(meterProvider);
    }
    counter({ name, value, labels, unit }) {
        if (!this.isEnabled)
            return;
        const meter = metrics.getMeter(name);
        const counter = meter.createCounter(name, {
            unit,
        });
        counter.add(value, { ...labels });
    }
    gauge({ name, value, labels, unit }) {
        if (!this.isEnabled)
            return;
        const meter = metrics.getMeter(name);
        const gauge = meter.createObservableGauge(name, {
            unit,
        });
        gauge.addCallback(result => {
            result.observe(value, { ...labels });
        });
    }
    histogram({ name, value, buckets, labels, unit }) {
        if (!this.isEnabled)
            return;
        const meter = metrics.getMeter(name);
        const histogram = meter.createHistogram(name, {
            unit,
            advice: {
                explicitBucketBoundaries: buckets,
            },
        });
        histogram.record(value, { ...labels });
    }
}
const clientMetrics = new ClientMetrics();
export default clientMetrics;
